import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createFolder } from '../../../api/folders';
import { useSnack } from '../../../hooks';
import { camelCase } from 'lodash';
import { IcrSuccessScreen, FolderCreateModalForm } from '@koob/booking';
import {
  KoobButton,
  KoobInputDateField,
  KoobTitle,
  KoobParagraph,
  KoobModal,
  KoobSelectLanguageField,
} from '@koob/ui';
import { SelectField, TextField } from 'components/Fields';
import SelectNationatilyField from 'components/Fields/SelectNationatilyField';
import { KoobInputCheckboxField } from '@koob/ui';
import { getLanguages } from '../../../api/languages';
import { useApp } from 'hooks';

export default function FoldersModal({
  open,
  setOpen,
  onFolderCreated,
  skipSuccessScreen = false,
}) {
  // Esc and the overlay go through the form's guard, which raises the discard
  // confirmation when the folder holds unsaved changes.
  const closeGuard = useRef(() => setOpen(false));
  const { t } = useTranslation('folders');
  const { notify } = useSnack();
  const { currentOrganization } = useApp();
  const defaultLocale = currentOrganization?.language ?? 'en';

  const [pending, setPending] = React.useState(false);
  const [createdIcr, setCreatedIcr] = React.useState(null);
  const [createdFolder, setCreatedFolder] = React.useState(null);

  const handleCreateCustomerFile = async values => {
    setPending(true);
    try {
      // The shared form already normalises everything the API expects, room
      // composition and traveller kind/position included: forward it as is.
      const result = await createFolder({
        ...values,
        icr: values.icr || undefined,
        locale: values.locale?.value ?? values.locale,
      });

      if (skipSuccessScreen) {
        onFolderCreated(result.data);
        return;
      }

      setCreatedFolder(result.data);
      setCreatedIcr(result.data.icr);
    } catch (err) {
      if (err.response && err.response.status === 409) {
        notify(t('modal.guests.existingIcr'), { type: 'error' });
        return;
      }
      if (err.response && err.response.status === 500) {
        notify(t('modal.guests.somethingWentWrong'), { type: 'error' });
        return;
      }
      const error = camelCase(err?.response?.data?.message ?? 'error');

      notify(t(`errors:${error}`), { type: 'error' });
    } finally {
      setPending(false);
    }
  };

  return (
    <KoobModal
      open={open}
      setOpen={() => closeGuard.current()}
      widthClass="sm:max-w-5xl overflow-visible"
      showCloseIcon
    >
      {createdIcr ? (
        <IcrSuccessScreen
          icr={createdIcr}
          onContinue={() => {
            setCreatedIcr(null);
            notify(t('success'));
            onFolderCreated(createdFolder);
            setOpen(false);
          }}
        />
      ) : (
        <>
          <div className="text-center max-w-lg mx-auto">
            <KoobTitle size="text-xl">{t('modal.title')}</KoobTitle>
            <KoobParagraph size="text-sm">
              {t('modal.description')}
            </KoobParagraph>
          </div>

          <FolderCreateModalForm
            onSubmit={values => handleCreateCustomerFile(values)}
            onCancel={() => setOpen(false)}
            isPending={pending}
            initialValues={{
              name: undefined,
              icr: undefined,
              locale: defaultLocale,
              travelers: [],
            }}
            renderLanguageField={() => (
              <KoobSelectLanguageField
                label={t('modal.tripLanguage')}
                name="locale"
                useTitleAsLabel
                required
                getLanguages={getLanguages}
                defaultLocale={defaultLocale}
              />
            )}
            renderTextField={props => (
              // The shared form passes touchRequired: false, which surfaces
              // "required" errors before any input — show them on touch only.
              <TextField {...props} touchRequired />
            )}
            renderSelectField={props => (
              <SelectField {...props} touchRequired />
            )}
            renderDateField={props => (
              <KoobInputDateField
                {...props}
                showPlaceHolder={!!props.label}
                datePlaceholder={props.label}
              />
            )}
            renderNationalityField={props => (
              <SelectNationatilyField {...props} touchRequired />
            )}
            renderCheckboxField={props => <KoobInputCheckboxField {...props} />}
            onRegisterCloseGuard={guard => (closeGuard.current = guard)}
            renderSubmitArea={({ isValid, onCancel }) => (
              <div className="flex justify-end space-x-4">
                <KoobButton
                  color="secondary"
                  style={{ minWidth: 80, minHeight: 40 }}
                  onClick={onCancel}
                >
                  {t('misc:cancel')}
                </KoobButton>

                <KoobButton
                  testId="save-change"
                  disabled={pending || !isValid}
                  type="submit"
                  variant="primary"
                  style={{ minWidth: 120, minHeight: 40 }}
                >
                  {t('misc:save')}
                </KoobButton>
              </div>
            )}
          />
        </>
      )}
    </KoobModal>
  );
}
